import { z } from "zod";

import {
  RUN_STATUSES,
  type InvoiceReviewFilters,
} from "../invoice-reviews/types.ts";

/**
 * La URL de la página maestra de documentos (`/work/documentos`): qué vista
 * se mira y, en la lista, sus filtros. Todo en la URL, como el mes (`?p`):
 * recargar no lo pierde y el enlace que se comparte enseña lo mismo.
 *
 * Lo que no se entiende se descarta (`catch`) en vez de llevar a la pantalla
 * de error: un filtro mal copiado no es motivo para no ver la página.
 */

/**
 * Las vistas: el Resumen («¿cómo vamos?»), la lista de documentos y los dos
 * detalles técnicos, el proceso y el acierto.
 */
export const DOCUMENT_VIEWS = [
  "resumen",
  "documentos",
  "proceso",
  "acierto",
] as const;

export type DocumentView = (typeof DOCUMENT_VIEWS)[number];

/**
 * La decisión final, como la filtra `list_document_reviews`: `none` es «sin
 * decidir» y `decided`, cualquiera de las tres (`in`).
 */
export const DECISION_FILTERS = [
  "valid",
  "invalid",
  "for_data",
  "none",
  "decided",
] as const;

export type DecisionFilter = (typeof DECISION_FILTERS)[number];

/** El router lee como número lo que parece un número: aquí todo es texto. */
const text = z
  .union([z.string(), z.number()])
  .transform((value) => String(value))
  .refine((value) => value !== "")
  .optional()
  .catch(undefined);

/** Un instante ISO 8601, como lo manda el API (`today.from`). */
const instant = z
  .string()
  .refine((value) => !Number.isNaN(Date.parse(value)))
  .optional()
  .catch(undefined);

export const documentsSearchSchema = z.object({
  vista: z.enum(DOCUMENT_VIEWS).optional().catch(undefined),
  status: z.enum(RUN_STATUSES).optional().catch(undefined),
  /** El tipo de archivo: un resultado de «Identificar qué es el archivo». */
  fileType: text,
  /** La clasificación: un resultado de «Identificar qué es el archivo». */
  photoType: text,
  /** El paso en el que está ahora. */
  step: text,
  /** Buscar un documento por su número. */
  documento: z.coerce.number().int().positive().optional().catch(undefined),
  /** La decisión final. */
  decision: z.enum(DECISION_FILTERS).optional().catch(undefined),
  /** Quién decidió: su id de auth. */
  decidedBy: text,
  /** Su nombre, solo para decirlo en el filtro. */
  decidedByName: text,
  /** Decididos desde (incluido) y hasta (sin incluir): un instante ISO. */
  decidedFrom: instant,
  decidedTo: instant,
  /** Por qué falló (`FAILURE_REASONS`): «Por el límite de la IA». */
  motivo: text,
});

export type DocumentsSearch = z.infer<typeof documentsSearchSchema>;

/** Los filtros de la lista, por su nombre en la URL. */
const LIST_KEYS = [
  "status",
  "fileType",
  "photoType",
  "step",
  "documento",
  "decision",
  "decidedBy",
  "decidedFrom",
  "decidedTo",
  "motivo",
] as const satisfies readonly (keyof DocumentsSearch)[];

/** Sin filtros de la lista: lo que se borra con «Quitar filtros». */
export const NO_LIST_FILTERS = Object.fromEntries(
  [...LIST_KEYS, "decidedByName"].map((key) => [key, undefined]),
) as Partial<DocumentsSearch>;

/**
 * La vista que se mira. Sin `vista`, el Resumen; pero un enlace con filtros
 * --`/work/documentos?status=waiting`-- abre la lista con esos filtros.
 */
export const viewOf = (search: DocumentsSearch): DocumentView =>
  search.vista ??
  (LIST_KEYS.some((key) => search[key] !== undefined)
    ? "documentos"
    : "resumen");

/**
 * Los filtros de la lista: los de la URL y el mes del panel (`?p`, la
 * bolsa). Los decididos un día van SIN mes: lo del día es de cualquier
 * bolsa.
 */
export const listFiltersOf = (
  search: DocumentsSearch,
  period: string,
): InvoiceReviewFilters => ({
  ...(search.status !== undefined ? { status: search.status } : {}),
  ...(search.fileType !== undefined ? { fileKind: search.fileType } : {}),
  ...(search.photoType !== undefined ? { photoType: search.photoType } : {}),
  ...(search.step !== undefined ? { stepKey: search.step } : {}),
  ...(search.documento !== undefined ? { documentId: search.documento } : {}),
  ...(search.decision !== undefined ? { decision: search.decision } : {}),
  ...(search.decidedBy !== undefined ? { decidedBy: search.decidedBy } : {}),
  ...(search.decidedFrom !== undefined
    ? { decidedFrom: search.decidedFrom }
    : {}),
  ...(search.decidedTo !== undefined ? { decidedTo: search.decidedTo } : {}),
  ...(search.motivo !== undefined ? { failureReason: search.motivo } : {}),
  /* Buscar por número es por identificador, no por bolsa: encuentra el
     documento sea del mes que sea. */
  ...(search.decidedFrom === undefined &&
  search.decidedTo === undefined &&
  search.documento === undefined
    ? { period }
    : {}),
});
